/**
 * Enum for submodel type identifiers
 */
export enum SubmodelType {
  DIGITAL_NAMEPLATE = 'dpp.submodel.digitalnameplate',
  HIERARCHICAL_STRUCTURE = 'dpp.submodel.hierarchicalstructure',
  HANDOVER_DOCUMENTATION = 'dpp.submodel.handoverdocumentation',
}

/**
 * Interface for submodel metadata including type and version.
 */
export interface SubmodelMetadata {
  type: SubmodelType;
  version: string;
}

/**
 * Mapping of semantic IDs to submodel types and versions.
 */
export const SEMANTIC_ID_MAP: Record<string, SubmodelMetadata> = {
  'https://admin-shell.io/idta/nameplate/3/0/Nameplate': {
    type: SubmodelType.DIGITAL_NAMEPLATE,
    version: '3.0',
  },
  'https://admin-shell.io/idta/HierarchicalStructures/1/1/Submodel': {
    type: SubmodelType.HIERARCHICAL_STRUCTURE,
    version: '1.1',
  },
  '0173-1#01-AHF578#003': {
    type: SubmodelType.HANDOVER_DOCUMENTATION,
    version: '2.0',
  },
};

/**
 * Retrieves metadata (type and version) for a given semantic ID.
 * @param semanticId - The semantic ID
 * @returns SubmodelMetadata if found, undefined otherwise
 */
export function getSubmodelMetadata(
  semanticId?: string,
): SubmodelMetadata | undefined {
  if (!semanticId) {
    return undefined;
  }
  return SEMANTIC_ID_MAP[semanticId];
}

const SUBMODEL_ICON_MAP: Partial<Record<SubmodelType, string>> = {
  [SubmodelType.DIGITAL_NAMEPLATE]: 'label',
  [SubmodelType.HIERARCHICAL_STRUCTURE]: 'hierarchy',
  [SubmodelType.HANDOVER_DOCUMENTATION]: 'book',
};

/**
 * Returns the icon name for a given submodel type.
 * @param type - The SubmodelType
 * @param fallback - Fallback icon name (default: 'document')
 */
export function getSubmodelIcon(
  type?: SubmodelType | string,
  fallback = 'document',
): string {
  if (!type) {
    return fallback;
  }
  return SUBMODEL_ICON_MAP[type as SubmodelType] ?? fallback;
}

/**
 * Maps each supported SubmodelType to its create route segment.
 * A type being present here also signals that it is fully supported for viewing.
 */
export const SUBMODEL_CREATE_ROUTE_SEGMENT: Partial<
  Record<SubmodelType, string>
> = {
  [SubmodelType.DIGITAL_NAMEPLATE]: 'create',
  [SubmodelType.HANDOVER_DOCUMENTATION]: 'create-handover-documentation',
};
